import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Building2,
  Code2,
  Shield,
  Zap,
  Brain,
  Clock,
  AlertTriangle,
  Target,
  CheckCircle,
  ArrowRight,
  TrendingUp,
  Award,
  Users,
  FileText,
  BarChart3,
  Rocket,
  Zap as Lightning,
  Code,
  Database,
  GitBranch,
  Cpu,
  Server,
  Cloud,
  Eye,
  Lock,
  Globe,
  Network,
  Monitor,
  Key,
  ShieldCheck,
  Activity,
  Settings
} from 'lucide-react';
import '../../styles/ServicePageCommon.css';
import './InfrastructureSecurity.css';

const InfrastructureSecurity = () => {
  const location = useLocation();
  const services = [
    { title: 'GCC as a Service', path: '/services/gcc', icon: Building2 },
    { title: 'Product Engineering', path: '/services/product-engineering', icon: Code2 },
    { title: 'Infrastructure & Security', path: '/services/infrastructure-security', icon: Shield },
    { title: 'Special Engagements', path: '/services/special-engagements', icon: Zap },
    { title: 'AI & Innovation', path: '/services/ai-innovation', icon: Brain }
  ];

  return (
    <div className="infrastructure-security-service-page">
      {/* Main Content */}
      <section className="service-content-section">
        <div className="service-header">
          <h1 className="service-title golden-gradient">IT Infrastructure & Security Consulting</h1>
          <p className="service-subtitle">
            Modern infrastructure. Resilient operations. Delivered with precision.
          </p>
        </div>

        <div className="content-layout">
          {/* Main Content Area */}
          <div className="service-main-content">

            <div className="content-section">
              <h2>Managed Infrastructure Services</h2>
              <p className="section-tagline">
                "Your infrastructure should be invisible—until it isn't. Cospin delivers fully managed infrastructure services that keep your systems running, secure, and scalable—so your teams can focus on innovation, not uptime."
              </p>

              <div className="why-matters">
                <h4>Why it matters:</h4>
                <ul>
                  <li><CheckCircle size={16} strokeWidth={2} />Reduces operational overhead and firefighting</li>
                  <li><CheckCircle size={16} strokeWidth={2} />Ensures 24/7 monitoring, patching, and performance tuning</li>
                  <li><CheckCircle size={16} strokeWidth={2} />Frees up internal teams to focus on product and customer experience</li>
                </ul>
              </div>

              <p className="section-highlight">
                With deep experience in cloud-native, containerized, and hybrid environments, we don't just manage infra—we optimize it for scale, security, and cost-efficiency.
              </p>
            </div>

            <div className="content-section">
              <h2>Multi-Cloud Solutions</h2>
              <p className="section-tagline">
                "Avoid vendor lock-in. Optimize for performance. Cospin helps you architect and operate across AWS, Azure, GCP, and beyond—so your workloads run where they perform best."
              </p>

              <div className="why-matters">
                <h4>Why it matters:</h4>
                <ul>
                  <li><CheckCircle size={16} strokeWidth={2} />Enables cost arbitrage and performance tuning</li>
                  <li><CheckCircle size={16} strokeWidth={2} />Improves resilience and compliance flexibility</li>
                  <li><CheckCircle size={16} strokeWidth={2} />Supports best-of-breed tooling across cloud providers</li>
                </ul>
              </div>

              <p className="section-highlight">
                We bring a cloud-agnostic mindset with hands-on expertise in multi-cloud orchestration, FinOps, and DevSecOps pipelines. Whether you're migrating, modernizing, or scaling—we've got you covered.
              </p>
            </div>

            <div className="content-section">
              <h2>Multi-Region Architecture</h2>
              <p className="section-tagline">
                "Downtime isn't an option. Cospin designs and deploys multi-region architectures that ensure your applications are always available—no matter where your users are."
              </p>

              <p className="section-highlight">
                Data residency requirements of certain regions will force your platform to be available across geo locations, some of these are regulatory driven and some are customer choices.
              </p>

              <div className="why-matters">
                <h4>Why it matters:</h4>
                <ul>
                  <li><CheckCircle size={16} strokeWidth={2} />Reduces latency for global users</li>
                  <li><CheckCircle size={16} strokeWidth={2} />Enables disaster recovery and business continuity</li>
                  <li><CheckCircle size={16} strokeWidth={2} />Meets data residency and compliance requirements</li>
                </ul>
              </div>

              <p className="section-highlight">
                From CDN strategies to active-active failover, we engineer for resilience. Our team has designed multi-region SaaS platforms and systems that never blink—even during regional outages.
              </p>
            </div>

            <div className="content-section">
              <h2>High Availability Design</h2>
              <p className="section-tagline">
                "We build for failure—so you don't have to worry about it. Cospin ensures your critical systems are architected for high availability, with no single point of failure."
              </p>

              <div className="why-matters">
                <h4>Why it matters:</h4>
                <ul>
                  <li><CheckCircle size={16} strokeWidth={2} />Keeps mission-critical systems online</li>
                  <li><CheckCircle size={16} strokeWidth={2} />Minimizes revenue loss and reputational risk</li>
                  <li><CheckCircle size={16} strokeWidth={2} />Supports SLAs and regulatory uptime requirements</li>
                </ul>
              </div>

              <p className="section-highlight">
                We understand enterprise-grade reliability and startup agility, we design HA systems that are cost-effective, observable, and self-healing.
              </p>
            </div>

            <div className="content-section">
              <h2>Secure Development Lifecycle</h2>
              <p className="section-tagline">
                "Security isn't a checkbox—it's a mindset. Cospin embeds security into every phase of your software lifecycle, from design to deployment."
              </p>

              <div className="why-matters">
                <h4>Why it matters:</h4>
                <ul>
                  <li><CheckCircle size={16} strokeWidth={2} />Reduces risk of breaches and compliance violations</li>
                  <li><CheckCircle size={16} strokeWidth={2} />Enables faster, safer releases</li>
                  <li><CheckCircle size={16} strokeWidth={2} />Builds trust with customers and regulators</li>
                </ul>
              </div>

              <p className="section-highlight">
                We bring DevSecOps into your pipelines, implement threat modeling, and automate SAST/SCA/DAST. With experience in healthtech and fintech compliance, we know what secure-by-design really means.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InfrastructureSecurity;
